import React, { useState, useCallback, useEffect } from "react";
import { Container, Row, Col, Modal, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { BiFilter } from "react-icons/bi";
import { GrPowerReset } from "react-icons/gr";
import moment from "moment";
import debounce from "lodash.debounce";
import { toast } from "react-toastify";

import "./../Rewards/With.css";
import { fetchDummyWallets } from "../../Actions/adminActions";
import PaginationComponent from "../../Component/Pagination/PaginationControls";
import apiCall from "../../API/API";

const DummyUserWallet = () => {
  const { dummyWallets } = useSelector((state) => state.admin);
  const dispatch = useDispatch();

  // --- STATE FOR TABLE & FILTERS ---
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState("none");
  const [search, setSearch] = useState({
    username: "",
    search: "",
    startDate: "",
    endDate: "",
    status: "1",
    uid: "",
  });
  const [limitFilter, setLimitFilter] = useState(20);
  const [dateFilter, setDateFilter] = useState("default");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // --- STATE FOR UPDATE MODAL ---
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
  const [editableWallets, setEditableWallets] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  // --- Wallets to exclude from display ---
  const excludedWallets = ["Total Payout", "Self Investment"];

  // --- Stats fields mapping ---
  const statsFields = [
    { slug: "total_income", name: "Total Income" },
    { slug: "total_order_amount", name: "Total Investment" },
    { slug: "total_capping_amount", name: "Total Capping Amount" },
    { slug: "earned_income", name: "Earned Income" },
    { slug: "business", name: "Total Business" },
    { slug: "today_business", name: "Today Business" },
    { slug: "total_team", name: "Total Team" },
    { slug: "prime_line", name: "Prime Line" },
    { slug: "support_line", name: "Support Line" },
  ];

  const fetchDummyWallet = useCallback(
    debounce((params) => {
      dispatch(fetchDummyWallets(params));
    }, 500),
    [dispatch]
  );

  // --- FILTER & PAGINATION HANDLERS ---
  const toggleFilter = () => setFilter(filter === "none" ? "flex" : "none");
  const handlePagination = (page) => setCurrentPage(page);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "startDate") {
      setStartDate(value);
    } else if (name === "endDate") {
      setEndDate(value);
    } else {
      setSearch((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleLimitChange = (e) => {
    setLimitFilter(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleDateFilterChange = (e) => {
    const value = e.target.value;
    setDateFilter(value);
    const today = moment();
    let start = "",
      end = "";
    switch (value) {
      case "today":
        start = today.startOf("day").format("YYYY-MM-DD");
        end = today.endOf("day").format("YYYY-MM-DD");
        break;
      case "yesterday":
        const yesterday = moment().subtract(1, "days");
        start = yesterday.startOf("day").format("YYYY-MM-DD");
        end = yesterday.endOf("day").format("YYYY-MM-DD");
        break;
      case "this_week":
        start = today.startOf("week").format("YYYY-MM-DD");
        end = today.endOf("week").format("YYYY-MM-DD");
        break;
      case "this_month":
        start = today.startOf("month").format("YYYY-MM-DD");
        end = today.endOf("month").format("YYYY-MM-DD");
        break;
      default:
        break;
    }
    setStartDate(start);
    setEndDate(end);
  };

  const addFilter = () => {
    setCurrentPage(1);
    const queryParams = {
      page: 1,
      limit: limitFilter,
      ...Object.fromEntries(Object.entries(search).filter(([_, v]) => v)),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
    };
    dispatch(fetchDummyWallets(queryParams));
  };

  const resetFilter = () => {
    setSearch({
      username: "",
      search: "",
      startDate: "",
      endDate: "",
      status: "1",
      uid: "",
    });
    setLimitFilter(20);
    setStartDate("");
    setEndDate("");
    setDateFilter("default");
    setCurrentPage(1);
    dispatch(fetchDummyWallets({ page: 1, limit: 20, status: "1" }));
  };

  // --- UPDATE MODAL HANDLERS ---
  const handleUpdateModalShow = (user) => {
    setSelectedUserForEdit(user);

    // merge wallets + stats into editableWallets
    const walletsToEdit = user.wallets.reduce((acc, wallet) => {
      acc[wallet.slug] = wallet.value;
      return acc;
    }, {});

    if (user.stats) {
      statsFields.forEach((field) => {
        walletsToEdit[field.slug] = user.stats[field.slug] ?? 0;
      });
    }

    setEditableWallets(walletsToEdit);
    setShowUpdateModal(true);
  };

  const handleUpdateModalClose = () => {
    setShowUpdateModal(false);
    setSelectedUserForEdit(null);
    setEditableWallets({});
    setIsUpdating(false);
  };

  const handleWalletValueChange = (slug, value) => {
    setEditableWallets((prev) => ({ ...prev, [slug]: value }));
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setIsUpdating(true);

    const walletUpdates = [];
    const statsUpdates = {};

    // Wallet updates
    selectedUserForEdit.wallets.forEach((originalWallet) => {
      const originalValue = originalWallet.value;
      const updatedValue = editableWallets[originalWallet.slug];
      if (String(originalValue) !== String(updatedValue)) {
        walletUpdates.push({
          slug: originalWallet.slug,
          value: updatedValue,
        });
      }
    });

    // Stats updates
    statsFields.forEach((field) => {
      const originalValue = selectedUserForEdit.stats?.[field.slug] ?? 0;
      const updatedValue = editableWallets[field.slug];
      if (String(originalValue) !== String(updatedValue)) {
        statsUpdates[field.slug] = updatedValue;
      }
    });

    if (walletUpdates.length === 0 && Object.keys(statsUpdates).length === 0) {
      toast.info("No changes were made.");
      handleUpdateModalClose();
      return;
    }

    try {

      console.log("Update payload body:", {
        uid: selectedUserForEdit.uid,
        walletUpdates: walletUpdates.map(w => ({ ...w, value: Number(w.value) })),
        statsUpdates: Object.fromEntries(
          Object.entries(statsUpdates).map(([k, v]) => [k, Number(v)])
        ),
      });
      
      await apiCall("POST", "/update_fake_user_wallet", {
        uid: selectedUserForEdit.uid,
        walletUpdates,
        statsUpdates,
      });
      toast.success("Wallets updated successfully!");
      handleUpdateModalClose();
      dispatch(
        fetchDummyWallets({
          page: currentPage,
          limit: limitFilter,
          ...Object.fromEntries(Object.entries(search).filter(([_, v]) => v)),
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
        })
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "An error occurred while updating."
      );
      setIsUpdating(false);
    }
  };

  // --- DATA FETCHING & EFFECT HOOKS ---
  useEffect(() => {
    const queryParams = {
      page: currentPage,
      limit: limitFilter,
      ...Object.fromEntries(Object.entries(search).filter(([_, v]) => v)),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
    };
    fetchDummyWallet(queryParams);
  }, [dispatch, currentPage, limitFilter, search, startDate, endDate, fetchDummyWallet]);

  useEffect(() => {
    if (dummyWallets?.totalPages) {
      setTotalPages(dummyWallets.totalPages);
    }
  }, [dummyWallets]);

  return (
    <React.Fragment>
      <Container>
        {/* --- FILTER UI --- */}
        <Row id="filterRow">
          <span id="filterLogo1" onClick={toggleFilter}>
            <i>
              <BiFilter />
            </i>{" "}
            Filter
          </span>
          <Row className="p-0 m-0" style={{ display: filter }}>
            <Col lg="2" md="4">
              <input
                type="text"
                name="username"
                placeholder="Search By Username"
                value={search.username}
                onChange={handleChange}
              />
            </Col>
            <Col lg="2" md="4">
              <input
                type="text"
                placeholder="Search User By Uid"
                name="uid"
                value={search.uid}
                onChange={handleChange}
              />
            </Col>
            <Col lg="2" md="4">
              <input
                type="date"
                name="startDate"
                value={startDate}
                onChange={handleChange}
              />
            </Col>
            <Col lg="2" md="4">
              <input
                type="date"
                name="endDate"
                value={endDate}
                onChange={handleChange}
              />
            </Col>
            <Col lg="2" md="4">
              <select
                name="dateFilter"
                value={dateFilter}
                onChange={handleDateFilterChange}
              >
                <option value="default">Default</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
              </select>
            </Col>
            <Col lg="2" md="4">
              <select value={limitFilter} onChange={handleLimitChange}>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
            </Col>
            <div>
              <button className="save filter" onClick={addFilter}>
                Filter{" "}
                <i>
                  <BiFilter />
                </i>
              </button>
              <button className="cancel reset" onClick={resetFilter}>
                Reset{" "}
                <i>
                  <GrPowerReset />
                </i>
              </button>
            </div>
          </Row>
        </Row>

        {/* --- WALLETS TABLE --- */}
        <div id="directIncomeDiv">
          <Row id="directTableRow" className="text-center">
            <Col md="12" className="p-0">
              <div style={{ width: "100%", overflowX: "auto" }}>
                <table>
                    <tr>
                      <th>S No.</th>
                      <th>UID</th>
                      <th>Name</th>
                      <th>Username</th>
                      {dummyWallets?.data?.[0]?.wallets
                        .filter((wallet) => !excludedWallets.includes(wallet.name))
                        .map((wallet) => (
                          <th key={wallet._id || wallet.slug}>{wallet.name}</th>
                        ))}
                      {/* add stats headers */}
                      {statsFields.map((field) => (
                        <th key={field.slug}>{field.name}</th>
                      ))}
                      <th>Action</th>
                    </tr>
                  <tbody>
                    {dummyWallets?.data?.map((user, i) => (
                      <tr key={user._id}>
                        <td>{i + 1 + limitFilter * (currentPage - 1)}</td>
                        <td>{user.uid}</td>
                        <td>{user.name}</td>
                        <td>{user.username}</td>
                        {user.wallets
                          .filter((wallet) => !excludedWallets.includes(wallet.name))
                          .map((wallet) => (
                            <td key={wallet._id || wallet.slug}>
                              {wallet.value ?? 0}
                            </td>
                          ))}
                        {/* stats values */}
                        {statsFields.map((field) => (
                          <td key={field.slug}>
                            {user.stats?.[field.slug] ?? 0}
                          </td>
                        ))}
                        <td>
                          <Button
                            variant="info"
                            size="sm"
                            onClick={() => handleUpdateModalShow(user)}
                          >
                            Update
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Col>
          </Row>
        </div>

        {/* --- PAGINATION --- */}
        <Row>
          <Col md="12">
            {totalPages > 1 && (
              <PaginationComponent
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePagination}
              />
            )}
          </Col>
        </Row>
      </Container>

      {/* --- UPDATE WALLETS MODAL --- */}
      <Modal 
        show={showUpdateModal} 
        onHide={handleUpdateModalClose} 
        centered 
        size="lg"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Update Wallets - {selectedUserForEdit?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUserForEdit && (
            <form onSubmit={handleUpdateSubmit}>
              {/* User Info Section */}
              <div className="mb-4 p-3 bg-light rounded">
                <h6 className="mb-2 text-primary">
                  <i className="fas fa-user me-2"></i>User Information
                </h6>
                <Row>
                  <Col md={4}>
                    <small className="text-muted">Name</small>
                    <div className="fw-bold">{selectedUserForEdit.name}</div>
                  </Col>
                  <Col md={4}>
                    <small className="text-muted">Username</small>
                    <div className="fw-bold">{selectedUserForEdit.username}</div>
                  </Col>
                  <Col md={4}>
                    <small className="text-muted">UID</small>
                    <div className="fw-bold">{selectedUserForEdit.uid}</div>
                  </Col>
                </Row>
              </div>

              {/* Wallets Section */}
              <div className="mb-4">
                <Row>
                  {selectedUserForEdit.wallets
                    .filter((wallet) => !excludedWallets.includes(wallet.name))
                    .map((wallet, index) => (
                      <Col key={wallet.slug} md={6} className="mb-3">
                        <label htmlFor={wallet.slug} className="form-label small">
                          {wallet.name}
                        </label>
                        <input
                          type="number"
                          id={wallet.slug}
                          className="form-control form-control-sm"
                          value={editableWallets[wallet.slug] ?? ""}
                          onChange={(e) =>
                            handleWalletValueChange(wallet.slug, e.target.value)
                          }
                          placeholder="0"
                        />
                      </Col>
                    ))}
                </Row>
              </div>

              {/* Stats Section */}
              <div className="mb-4">
                <Row>
                  {statsFields.map((field) => (
                    <Col key={field.slug} md={6} className="mb-3">
                      <label htmlFor={field.slug} className="form-label small">
                        {field.name}
                      </label>
                      <input
                        type="number"
                        id={field.slug}
                        className="form-control form-control-sm"
                        value={editableWallets[field.slug] ?? "0"}
                        onChange={(e) =>
                          handleWalletValueChange(field.slug, e.target.value)
                        }
                        placeholder="0"
                      />
                    </Col>
                  ))}
                </Row>
              </div>

              {/* Action Buttons */}
              <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                <Button
                  variant="outline-secondary"
                  onClick={handleUpdateModalClose}
                  className="me-2"
                  size="sm"
                >
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={isUpdating}
                  size="sm"
                >
                  {isUpdating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Updating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default DummyUserWallet;
